import { withoutBody } from "@/lib/blog";
import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import Layout from "@/components/Layout";
import PageTitle from "@/components/PageTitle";
import BlogCard from "@/components/BlogCard";
import { getPost, getPosts } from "@/lib/cms";
import { formatDetailDate, type BlogPost } from "@/lib/blog";
import { SITE_URL } from "@/lib/seo";

// Replaces $route['(:any)'] => Welcome/blogdetail/$1 from the CI routes file.
// Post bodies are HTML authored in the CI admin's rich-text editor and are
// rendered as-is, exactly as blog-detail.php did.

type Props = { post: BlogPost; recent: BlogPost[]; related: BlogPost[] };

export default function BlogDetail({ post, recent, related }: Props) {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.heading,
    description: post.description,
    image: post.image ? `${SITE_URL}${post.image}` : undefined,
    datePublished: post.date || undefined,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Chest Surgery India",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/img/logotho.png` },
    },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}/`,
  };

  return (
    <Layout
      route={`/blog/${post.slug}`}
      seo={{
        title: post.title || post.heading,
        description: post.description,
        canonical: `/blog/${post.slug}`,
        image: post.image,
        type: "article",
      }}
      schemas={[articleSchema]}
      // Posts written in the CMS have /hi and /ar versions; the 190 migrated
      // out of the old MySQL blog stay single-language.
      localizable={post.source === "cms"}
    >
      <PageTitle title={post.heading} crumb="Blog Details" />

      <div className="blog-details-area pt-100">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="blog-details-item">
                {post.image && (
                  <div className="blog-details-img">
                    <img src={post.image} alt={post.heading} />
                  </div>
                )}
                <ul className="blog-details-meta">
                  <li>
                    <i className="icofont-businessman" /> {post.author}
                  </li>
                  <li>
                    <i className="icofont-calendar" /> {formatDetailDate(post.date)}
                  </li>
                  {post.category && (
                    <li>
                      <i className="icofont-tag" /> {post.category}
                    </li>
                  )}
                </ul>

                <div
                  className="blog-details-body"
                  dangerouslySetInnerHTML={{ __html: post.body }}
                />
              </div>

              <div className="doctor-card mt-5 p-4">
                <div className="row align-items-center">
                  <div className="col-md-3 text-center">
                    <img
                      src="/assets/img/drim.webp"
                      alt="Dr. Parveen Yadav"
                      className="img-fluid doctor-img"
                    />
                  </div>
                  <div className="col-md-9">
                    <Link href="/about">
                      <h3 className="doctor-name">Dr. Parveen Yadav</h3>
                    </Link>
                    <p className="doctor-designation">
                      18+ Yrs Exp | 5,700+ Thoracic &amp; Robotic Cancer Surgeries
                    </p>
                    <p className="doctor-description">
                      Dr. Parveen Yadav is a Director and Senior Consultant in Thoracic and
                      Surgical Oncology, specializing in minimally invasive and robotic lung
                      and esophageal surgeries, with advanced training from AIIMS and Tata
                      Memorial Hospital.
                    </p>
                    <Link href="/about" className="doctor-btn">
                      View Full Profile
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="blog-details-item">
                <div className="blog-details-recent">
                  <h3>Recent Posts</h3>
                  <ul>
                    {recent.map((r) => (
                      <li key={r.slug}>
                        <Link href={`/blog/${r.slug}`}>
                          <img src={r.image || "/assets/img/logotho.png"} alt={r.heading} />
                        </Link>
                        <Link href={`/blog/${r.slug}`}>
                          <h4>{r.heading}</h4>
                        </Link>
                        <ul>
                          <li>
                            <i className="icofont-businessman" /> {r.author}
                          </li>
                          <li>
                            <i className="icofont-calendar" /> {formatDetailDate(r.date)}
                          </li>
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="blog-area-two pt-100 pb-70">
          <div className="container">
            <div className="section-title">
              <h2>Related Posts</h2>
            </div>
            <div className="row">
              {related.map((r) => (
                <BlogCard key={r.slug} post={r} />
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getPosts();
  return {
    paths: posts.map((p) => ({ params: { slug: p.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props, { slug: string }> = async ({ params }) => {
  const post = await getPost(params!.slug);
  if (!post) return { notFound: true };

  const all = await getPosts();
  const others = all.filter((p) => p.slug !== post.slug);

  return {
    props: {
      post,
      recent: others.slice(0, 4).map(withoutBody),
      // Same-category posts first; topped up with recent ones if thin.
      related: [
        ...others.filter((p) => p.category && p.category === post.category),
        ...others.filter((p) => p.category !== post.category),
      ].slice(0, 3).map(withoutBody),
    },
  };
};
